import type {SurveyEvaluationRequestDTO}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import api from "crqlar-react-common/dist/services/api";
import {enqueueSnackbar} from "notistack";
import {SurveyEvaluationFilterState} from "../../shared/SurveyEvaluationFilters";
import {downloadBlobAsFile, exportDaySlug} from "../../shared/surveyExport.util";

export type SurveyReportFormat = 'pdf' | 'docx' | 'xlsx';

const REPORT_URI = '/v3/aggregation/survey/report/export';

// The server builds and renders the whole report in this one request; a busy survey over a long period takes a while.
const REPORT_TIMEOUT_MS = 120000;

/** The API's file-name slug (`ExportText.slug`): German letters spelled out, other accents dropped, at most 60 characters. */
const reportSlug = (value: string | undefined, fallback: string) => {
    const slug = (value ?? '')
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
        .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
        .replace(/ß/g, 'ss')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60)
        .replace(/^-+|-+$/g, '');

    return slug || fallback;
};

/**
 * Only for a response that names no file: the name the API gives the report, the survey and the evaluated days,
 * `gaestebefragung-2026-09-01_2026-09-30.pdf`.
 */
export const surveyReportFallbackFileName = (
    survey: Survey,
    filters: SurveyEvaluationFilterState,
    format: SurveyReportFormat
) => `${reportSlug(survey.name, 'survey')}-${exportDaySlug(filters.range.start)}_${exportDaySlug(filters.range.end)}.${format}`;

/**
 * The file name in a Content-Disposition header. The API sends an ASCII name (every name it makes) as a plain
 * `filename="…"`; a name with other characters also as RFC 5987 `filename*=UTF-8''…` (percent-encoded UTF-8), which
 * wins, next to an RFC 2047 `filename="=?UTF-8?Q?…?="` twin that is not decoded here.
 */
export const contentDispositionFileName = (header?: string | null): string | undefined => {
    if (!header) return undefined;

    const extended = /filename\*\s*=\s*[^']*'[^']*'([^;\s]+)/i.exec(header);
    if (extended) {
        try {
            const name = decodeURIComponent(extended[1]);
            if (name) return name;
        } catch {
            // Not valid percent-encoding: the plain name may still do.
        }
    }

    const plain = /(?:^|;)\s*filename\s*=\s*(?:"((?:\\.|[^"\\])*)"|([^;\s]+))/i.exec(header);
    const name = (plain?.[1]?.replace(/\\(.)/g, '$1') ?? plain?.[2])?.trim();
    return name && !name.startsWith('=?') ? name : undefined;
};

const readBlobText = (blob: Blob) => typeof blob.text === 'function'
    ? blob.text()
    : new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });

/**
 * Why the API refused the report, in the user's language: the `message` of its JSON error body, for a 4xx or a 503
 * (too many exports at once). A download request receives that body as a Blob, not as parsed JSON. Undefined for
 * anything else (network errors, other server errors, a body without a message).
 */
export const surveyReportErrorMessage = async (error: unknown): Promise<string | undefined> => {
    const response = (error as {response?: {status?: number, data?: unknown}} | null | undefined)?.response;
    const status = response?.status ?? 0;
    if (!((status >= 400 && status < 500) || status === 503)) return undefined;

    try {
        const data = response?.data;
        const body: unknown = data instanceof Blob ? JSON.parse(await readBlobText(data))
            : typeof data === 'string' ? JSON.parse(data)
                : data;
        const message = (body as {message?: unknown} | null | undefined)?.message;
        return typeof message === 'string' && message.trim() ? message.trim() : undefined;
    } catch {
        return undefined;
    }
};

/**
 * Downloads the survey report the API renders for an evaluation request (the evaluation page's survey, period and
 * filters, with the dialog's sensitive opt-in), written in `language` and saved under the name the API gives it.
 *
 * Resolves `true` once the file is saved. When the API refuses the report with a reason (a 4xx, or a 503 because too
 * many exports run at once), that reason is shown as an error snackbar and it resolves `false`; any other failure
 * rejects, for the caller's generic error message.
 */
export const downloadSurveyReport = async ({request, format, language, fallbackFileName}: {
    request: SurveyEvaluationRequestDTO
    format: SurveyReportFormat
    language?: string
    fallbackFileName: string
}): Promise<boolean> => {
    const response = await api.post<Blob>(REPORT_URI, request, {
        params: {format, language},
        responseType: 'blob',
        timeout: REPORT_TIMEOUT_MS,
    }).catch(async (error: unknown) => {
        const message = await surveyReportErrorMessage(error);
        if (!message) throw error;

        enqueueSnackbar(message, {variant: 'error', anchorOrigin: {vertical: 'bottom', horizontal: 'center'}});
        return undefined;
    });
    if (!response) return false;

    const disposition = response.headers['content-disposition'];
    const fileName = contentDispositionFileName(typeof disposition === 'string' ? disposition : undefined);

    downloadBlobAsFile(response.data, fileName ?? fallbackFileName);
    return true;
};
